import {ClaudeCodeIcon} from '@github-ui/coding-tools-settings/ClaudeCodeIcon'
import {CodexIcon} from '@github-ui/coding-tools-settings/CodexIcon'
import {getToolDisplayName} from '@github-ui/coding-tools-settings/types'
import {getCookie} from '@github-ui/cookies'
import {copyText} from '@github-ui/copy-to-clipboard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {AgentIcon, CopyIcon, TerminalIcon, ToolsIcon, VscodeIcon} from '@primer/octicons-react'
import type {ActionMenuButtonProps} from '@primer/react'
import {ActionList, ActionMenu, Tooltip, Truncate} from '@primer/react'
import type {RefObject} from 'react'
import {useCallback, useMemo} from 'react'

import styles from './CodingAssistantButton.module.css'

type CodingAssistantButtonProps = {
  url: string
  buttonLabel: string
  promptBuilder: (issueUrl: string) => string
  size: ActionMenuButtonProps['size']
  variant?: ActionMenuButtonProps['variant']
  iconOnly?: boolean
  showIcon?: boolean
  className?: string
  onAssignToAgent?: () => void
  anchorRef?: RefObject<HTMLElement | null>
}

function getEnabledTools(): Record<string, boolean> | undefined {
  const cookie = getCookie('preferred_coding_tools')
  if (!cookie) return undefined
  try {
    return JSON.parse(decodeURIComponent(cookie.value)) as Record<string, boolean>
  } catch {
    return undefined
  }
}

const CODING_TOOLS_SETTINGS_PATH = '/settings/coding_tools'

function buildVSCodeAgentUrl(prompt: string): string {
  const doubleEncoded = encodeURIComponent(encodeURIComponent(prompt))
  return `vscode://github.copilot-chat?mode=agent&prompt=${doubleEncoded}`
}

function buildCodexUrl(prompt: string): string {
  const encoded = encodeURIComponent(prompt)
  return `codex://new?prompt=${encoded}`
}

function buildClaudeCodeUrl(prompt: string): string {
  const encoded = encodeURIComponent(prompt)
  return `https://claude.ai/code?prompt=${encoded}`
}

const assistants = [
  {icon: VscodeIcon, toolKey: 'copilot_vs_code', urlBuilder: buildVSCodeAgentUrl},
  {icon: CodexIcon, toolKey: 'codex_desktop_app', urlBuilder: buildCodexUrl},
  {icon: ClaudeCodeIcon, toolKey: 'claude_code_web', urlBuilder: buildClaudeCodeUrl},
]

type CodingAssistantMenuItemsProps = {
  prompt: string
  showSettingsLink?: boolean
  onAssignToAgent?: () => void
  hideOpenInHeading?: boolean
}

export function CodingAssistantMenuItems({
  prompt,
  showSettingsLink,
  onAssignToAgent,
  hideOpenInHeading,
}: CodingAssistantMenuItemsProps) {
  const enabledTools = getEnabledTools()
  const filteredAssistants = enabledTools ? assistants.filter(a => enabledTools[a.toolKey]) : assistants

  const handleCopyAsPrompt = useCallback(() => {
    void copyText(prompt)
  }, [prompt])

  return (
    <ActionList>
      <ActionList.Item onSelect={handleCopyAsPrompt}>
        <ActionList.LeadingVisual>
          <TerminalIcon />
        </ActionList.LeadingVisual>
        Copy as prompt
        <ActionList.Description variant="block">
          <Truncate maxWidth={240} title="">
            {prompt}
          </Truncate>
        </ActionList.Description>
        <ActionList.TrailingVisual>
          <CopyIcon />
        </ActionList.TrailingVisual>
      </ActionList.Item>
      {onAssignToAgent && (
        <ActionList.Item onSelect={onAssignToAgent}>
          <ActionList.LeadingVisual>
            <AgentIcon />
          </ActionList.LeadingVisual>
          Cloud agent
        </ActionList.Item>
      )}
      {filteredAssistants.length > 0 && (
        <>
          <ActionList.Divider className={styles.divider} />
          <ActionList.Group>
            {!hideOpenInHeading && <ActionList.GroupHeading>Open in</ActionList.GroupHeading>}
            {filteredAssistants.map(({icon: Icon, toolKey, urlBuilder}) => (
              <ActionList.Item key={toolKey} onSelect={() => window.open(urlBuilder(prompt), '_self')}>
                <ActionList.LeadingVisual>
                  <Icon />
                </ActionList.LeadingVisual>
                {getToolDisplayName(toolKey)}
              </ActionList.Item>
            ))}
          </ActionList.Group>
        </>
      )}
      {showSettingsLink && (
        <>
          <ActionList.Divider className={styles.divider} />
          <ActionList.LinkItem href={CODING_TOOLS_SETTINGS_PATH}>
            <ActionList.LeadingVisual>
              <ToolsIcon />
            </ActionList.LeadingVisual>
            Configure coding tools
          </ActionList.LinkItem>
        </>
      )}
    </ActionList>
  )
}

export function CodingAssistantButton({
  url,
  buttonLabel,
  promptBuilder,
  size,
  variant,
  iconOnly = false,
  showIcon = true,
  className,
  onAssignToAgent,
  anchorRef,
}: CodingAssistantButtonProps) {
  const prompt = useMemo(() => promptBuilder(url), [promptBuilder, url])
  const showSettingsLink = isFeatureEnabled('coding_tool_settings')

  return (
    <ActionMenu anchorRef={anchorRef}>
      {iconOnly ? (
        <Tooltip text={buttonLabel} type="label">
          <ActionMenu.Button className={className} leadingVisual={TerminalIcon} size={size} variant={variant} />
        </Tooltip>
      ) : (
        <ActionMenu.Button
          aria-label={buttonLabel}
          className={className}
          leadingVisual={showIcon ? TerminalIcon : undefined}
          size={size}
          variant={variant}
        >
          {buttonLabel}
        </ActionMenu.Button>
      )}
      <ActionMenu.Overlay className={styles.overlay}>
        <CodingAssistantMenuItems
          prompt={prompt}
          showSettingsLink={showSettingsLink}
          onAssignToAgent={onAssignToAgent}
        />
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
